import { Website } from '@superfunnel/types';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useAgentChat } from '../../contexts/AgentChatContext';
import { WebsiteUpdate } from '../../contexts/WebsiteUpdaterContext';
import { useEditorLogic, UseEditorReturn } from './hooks/useEditor';
import { ModalStates } from './hooks/useEditorModals';

export type EditorDevice = 'desktop' | 'mobile' | 'tablet';

// Re-export types that components might need
export type { ModalStates, Website, WebsiteUpdate };

// Simple intersection type combining basic editor props with all hook returns
type EditorContextType = UseEditorReturn & {
  onClose: () => void;
};

const EditorContext = createContext<EditorContextType | undefined>(undefined);

export const useEditor = () => {
  const context = useContext(EditorContext);
  if (context === undefined) {
    throw new Error('useEditor must be used within an EditorProvider');
  }
  return context;
};

interface GlobalSectionEditorProviderProps {
  mode: 'globalSection';
  globalSectionId: string;
}

interface WebsiteEditorProviderProps {
  mode: 'website';
  dynamicPageId: string;
  initialVariantId: string;
  initialWebsiteId: string;
}

type EditorProviderProps = (
  | GlobalSectionEditorProviderProps
  | WebsiteEditorProviderProps
) & {
  children: ReactNode;
  onClose: () => void;
  initialShowAnalytics?: boolean;
};

export const EditorProvider = ({
  children,
  initialShowAnalytics,
  ...props
}: EditorProviderProps) => {
  // Use our comprehensive hook that orchestrates everything
  const editorLogic = useEditorLogic({ ...props, initialShowAnalytics });
  const { setAgentContext } = useAgentChat();

  // Update agent context when variant/website changes
  useEffect(() => {
    if (props.mode === 'website') {
      const newContext = {
        dynamicPageId: props.dynamicPageId,
        variantId: editorLogic.selectedVariantId || props.initialVariantId,
        websiteId: editorLogic.selectedWebsiteId || props.initialWebsiteId,
      };

      console.log('🔄 Updating agent context:', newContext);
      setAgentContext(newContext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorLogic.selectedVariantId, editorLogic.selectedWebsiteId]);

  return (
    <EditorContext.Provider value={{ ...editorLogic, onClose: props.onClose }}>
      {children}
    </EditorContext.Provider>
  );
};
